import { useMemo, useState } from "react";
import type { CarOptionSet } from "@/types/database";
import { resolveCarMakeLogo } from "@/lib/car-make-logos";
import { useCarMakeLogos } from "@/lib/queries";
import { formatNumber } from "@/lib/utils";
import { MakeLogo } from "@/components/cars/MakeModelGrid";
import { OptionSetWizard } from "@/components/cars/OptionSetWizard";
import { Button } from "@/components/ui/Button";
import { Workspace, WorkspaceSection } from "@/components/ui/Workspace";

function ItemList({ title, items }: { title: string; items: string[] }) {
  return (
    <div>
      <h2 className="mb-4 font-bold">{title}</h2>
      {items.length === 0 ? (
        <p className="text-sm text-muted">None configured.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {items.map((item, index) => (
            <span key={`${title}-${item}-${index}`} className="stat-pill">
              {item}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function OptionSetDetailPanel({ optionSet }: { optionSet: CarOptionSet }) {
  const { data: makeLogos } = useCarMakeLogos();
  const [editOpen, setEditOpen] = useState(false);

  const logoEntry = useMemo(
    () => (optionSet.make && makeLogos ? resolveCarMakeLogo(optionSet.make, makeLogos) : null),
    [optionSet.make, makeLogos]
  );

  const models = optionSet.models ?? [];
  const trims = optionSet.trims ?? [];

  return (
    <>
      <Workspace>
        <WorkspaceSection className="p-4 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <MakeLogo make={optionSet.make} logo={logoEntry} className="h-16 w-16 object-contain" />
              <div>
                <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{optionSet.make}</h1>
                <p className="mt-1 text-sm text-muted">
                  {formatNumber(models.length)} {models.length === 1 ? "model" : "models"}
                  {trims.length > 0
                    ? ` · ${formatNumber(trims.length)} ${trims.length === 1 ? "trim" : "trims"}`
                    : null}
                </p>
              </div>
            </div>
            <Button variant="ghost" onClick={() => setEditOpen(true)}>
              Edit
            </Button>
          </div>
        </WorkspaceSection>

        <WorkspaceSection border className="grid gap-0 lg:grid-cols-2">
          <div className="border-b border-border p-4 sm:p-6 lg:border-b-0 lg:border-r">
            <ItemList title="Models" items={models} />
          </div>
          <div className="p-4 sm:p-6">
            <ItemList title="Trims" items={trims} />
          </div>
        </WorkspaceSection>
      </Workspace>

      <OptionSetWizard open={editOpen} onClose={() => setEditOpen(false)} optionSet={optionSet} />
    </>
  );
}
